import { useMutation, useReactiveVar } from '@apollo/client'
import coverDefault from '@assets/cover-default.svg'
import { Modal } from '@components/design-system/Modal'
import { ref, uploadBytes } from '@firebase/storage'
import { Profile, ProfileData, ProfileUpdateVars, UPDATE_ACCOUNT } from '@graphql/nftfy/backend/profile/Profile'
import { themeVar } from '@graphql/variables/SharedVariable'
import { accountVar } from '@graphql/variables/WalletVariable'
import { useAccount } from '@hooks/AccountHooks'
import { notifySuccess } from '@services/NotificationService'
import { formatShortAddressWallet } from '@services/UtilService'
import { Button, Form, Input, Upload } from 'antd'
import { useEffect, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import styled from 'styled-components'
import { externalUrl, storage } from '../../firebase'
import Icon, { Icons } from '../shared/Icon'
import ProfileAvatar from '../shared/ProfileAvatar'

interface ProfileHeaderProps {
  account: string
}

export const ProfileCover = ({ account }: ProfileHeaderProps) => {
  const theme = useReactiveVar(themeVar)
  const [editAvatar, setEditAvatar] = useState(false)
  const [loadingImg, setLoadingImg] = useState(false)
  const [editCover, setEditCover] = useState(false)
  const [editProfile, setEditProfile] = useState(false)

  const [showEditImage, setShowEditImage] = useState<'cover' | 'profile' | false>(false)

  const accountLogged = useReactiveVar(accountVar)
  const [updateProfile, { loading, data }] = useMutation<ProfileData, ProfileUpdateVars>(UPDATE_ACCOUNT)
  const { user, refetch } = useAccount(account)

  const isLoggedUser = accountLogged === account

  const handleImageUpload = async (file: any, type: 'cover' | 'profile' | false): Promise<boolean> => {
    if (!isLoggedUser) {
      return false
    }

    const path = `account/${type}/${account}`
    const storageRef = ref(storage, path)
    if (!account || type === false) {
      return false
    }
    try {
      setLoadingImg(true)
      await uploadBytes(storageRef, file?.file?.originFileObj)
      const url = `${externalUrl}${type}%2F${account}?${Date.now()}&alt=media`

      const uploadFile: Profile = type === 'cover' ? { coverUrl: url } : { avatarUrl: url }
      setLoadingImg(false)
      setShowEditImage(false)
      updateProfile({
        variables: {
          accountId: account.toLocaleLowerCase(),
          profile: {
            ...uploadFile
          }
        }
      })
      refetch()
      return true
    } catch (e) {
      setLoadingImg(false)
      return false
    }
  }
  const handleSubmit = (values: Profile) => {
    if (account) {
      updateProfile({
        variables: {
          accountId: account.toLocaleLowerCase(),
          profile: values
        }
      })
    }
  }
  useEffect(() => {
    if (data && !loading) {
      notifySuccess('Profile updated!')
      setEditProfile(false)
      refetch()
    }
  }, [data, loading, refetch])

  const formatUrl = (url: string) => {
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`
    }

    return url
  }

  return (
    <Container>
      <Avatar>
        <Cover
          coverUrl={user?.coverUrl ?? coverDefault.src}
          className={editCover ? 'edit' : ''}
          onMouseLeave={() => setEditCover(false)}
          onMouseOver={() => {
            isLoggedUser ? setEditCover(true) : setEditCover(false)
          }}
          onClick={() => {
            isLoggedUser ? setShowEditImage('cover') : setEditCover(false)
          }}
          color={theme}
        />
        <div>
          <ProfileAvatar
            width={144}
            height={144}
            avatarUrl={user?.avatarUrl}
            account={account ?? ''}
            circle
            className={`${editAvatar ? 'edit' : ''} ${theme}`}
            onMouseLeave={() => setEditAvatar(false)}
            onMouseOver={() => {
              isLoggedUser ? setEditAvatar(true) : setEditAvatar(false)
            }}
            action={() => {
              isLoggedUser ? setShowEditImage('profile') : setEditAvatar(false)
            }}
          />
          {accountLogged === account && <EditButton onClick={() => setEditProfile(true)}>Edit Profile</EditButton>}
        </div>
      </Avatar>
      <GeneralInfo className={theme}>
        <h2>{user?.username ?? 'Username'}</h2>
        <div>
          <span>{account ? formatShortAddressWallet(account, 13, true) : ''}</span>
        </div>
        <h3>{user?.description ?? 'No description'}</h3>
        <div>
          {user?.twitter && (
            <a target='_blank' href={`https://twitter.com/${user?.twitter}`} rel='noreferrer'>
              <Icon name={Icons.twitter} size={14} />
            </a>
          )}
          {user?.instagram && (
            <a target='_blank' href={`https://www.instagram.com/${user?.instagram}`} rel='noreferrer'>
              <Icon name={Icons.instagram} size={14} />
            </a>
          )}
          {user?.telegram && (
            <a target='_blank' href={`https://t.me/${user?.telegram}`} rel='noreferrer'>
              <Icon name={Icons.telegram} size={14} />
            </a>
          )}
          {user?.website && (
            <a target='_new' href={formatUrl(user?.website)} rel='external noreferrer'>
              <Icon name={Icons.hyperlink} size={14} />
            </a>
          )}
        </div>
      </GeneralInfo>
      <Modal
        isOpen={showEditImage !== false}
        onClose={() => {
          setShowEditImage(false)
        }}
      >
        <ContainerModalImages>
          <div>
            <h2>Upload a {showEditImage} image</h2>
            <Button onClick={() => setShowEditImage(false)}>
              <Icon name={Icons.close} size={24} />
            </Button>
          </div>
          <Upload maxCount={1} accept='image/png,image/jpeg' onChange={e => handleImageUpload(e, showEditImage)} disabled={loadingImg}>
            <Button>Upload {showEditImage}</Button>
          </Upload>
        </ContainerModalImages>
      </Modal>{' '}
      <Modal
        isOpen={editProfile}
        onClose={() => {
          setEditProfile(false)
        }}
      >
        <ContainerInfoProfile>
          <header>
            <h2>Edit profile</h2>
          </header>
          <Form autoComplete='off' onFinish={handleSubmit}>
            <div>
              <TitleModal>Username</TitleModal>
              <Form.Item
                name='username'
                initialValue={user?.username}
                rules={[{ pattern: /^[A-Za-z0-9._]{1,50}$/, message: 'Please input a valid username' }]}
              >
                <Input type='text' value={user?.username} />
              </Form.Item>
            </div>
            <div>
              <TitleModal>Email (optional) Get Nftfy exclusive content</TitleModal>
              <Form.Item name='email' initialValue={user?.email} rules={[{ type: 'email', message: 'Please input a valid email' }]}>
                <Input type='text' />
              </Form.Item>
            </div>
            <div>
              <TitleModal>Description</TitleModal>
              <Form.Item name='description' initialValue={user?.description}>
                <Input type='text' />
              </Form.Item>
            </div>
            <div>
              <TitleModal>Website URL</TitleModal>
              <Form.Item name='website' initialValue={user?.website}>
                <Input type='text' />
              </Form.Item>
            </div>
            <div>
              <TitleModal>Twitter Username</TitleModal>
              <Form.Item name='twitter' initialValue={user?.twitter}>
                <Input type='text' />
              </Form.Item>
              <small>Sample: @nftfy</small>
            </div>
            <div>
              <TitleModal>Instagram Username</TitleModal>
              <Form.Item name='instagram' initialValue={user?.instagram}>
                <Input type='text' />
              </Form.Item>
              <small>Sample: @nftfy</small>
            </div>
            <div>
              <TitleModal>Telegram Username</TitleModal>
              <Form.Item name='telegram' initialValue={user?.telegram}>
                <Input type='text' />
              </Form.Item>
            </div>
            <div>
              <SaveProfile type='primary' htmlType='submit' loading={loading}>
                Save
              </SaveProfile>
            </div>
          </Form>
        </ContainerInfoProfile>
      </Modal>
    </Container>
  )
}

export const {
  Container,
  Cover,
  TitleModal,
  Avatar,
  EditButton,
  GeneralInfo,
  ContainerModalImages,
  ContainerInfoProfile,
  Copy,
  SaveProfile
} = {
  Container: styled.div`
    display: grid;
    grid-template-rows: 1fr 50px 30px 30px;
    grid-row-gap: 8px;
    justify-items: center;
    position: relative;
    padding-bottom: 60px;
    border-bottom: 1px solid #e8e8e8;
    margin-bottom: 40px;
  `,
  Avatar: styled.div`
    width: 100%;
    display: grid;
    grid-template-rows: 1fr 50px 32px;
    grid-template-columns: 1fr;
    position: relative;

    > div:nth-child(2) {
      display: grid;
      div:nth-child(1) {
        grid-column-start: 1;
        grid-column-end: 3;
        justify-self: center;
        margin-top: -155px;
        &.edit {
          span:after {
            content: 'Edit';
            color: ${props => props.theme.colors.white};
            font-size: 16px;
            justify-content: center;
            align-items: center;
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: ${props => props.theme.colors.black};
            opacity: 0.8;
            display: flex;
            cursor: pointer;
          }
        }
        &.dark {
          span:after {
            background: ${props => props.theme.colors.constants.black};
            color: ${props => props.theme.colors.constants.white};
          }
        }
      }
    }
  `,
  Cover: styled.div<{ coverUrl: string; color: string }>`
    width: 100%;
    height: 265px;
    background: url(${props => props.coverUrl}) no-repeat;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 24px;
    grid-column-start: 1;
    grid-column-end: 3;
    &.edit:after {
      content: 'Edit';
      border-radius: 24px;
      color: ${({ theme, color }) => (color === 'dark' ? theme.colors.constants.white : theme.colors.black)};
      font-size: 18px;
      justify-content: center;
      align-items: center;
      position: absolute;
      width: 100%;
      height: 265px;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${({ theme, color }) => (color === 'dark' ? theme.colors.constants.black : theme.colors.black)};
      opacity: 0.8;
      display: flex;
      cursor: pointer;
    }
  `,
  EditButton: styled(Button)`
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    display: flex;
    align-items: center;
    border-radius: 8px;
    color: ${props => props.theme.colors.black};
    border: 2px solid ${props => props.theme.colors.gray[1]};
    padding: 20px 16px;
    background: transparent;
    margin-top: 30px;
    &:hover,
    &:focus {
      color: ${props => props.theme.colors.black};
      border: 2px solid ${props => props.theme.colors.gray[1]};
      background: ${props => props.theme.colors.gray[1]};
    }
    grid-column-start: 1;
    grid-column-end: 3;
    grid-row-start: 1;
    justify-self: flex-end;
  `,
  GeneralInfo: styled.div`
    display: grid;
    grid-template-rows: 1fr 1fr;
    grid-row-gap: 8px;
    h2 {
      justify-self: center;
      font-style: normal;
      font-weight: 500;
      font-size: 32px;
      line-height: 100%;
      color: ${props => props.theme.colors.black};
    }
    > div:nth-child(2) {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      span {
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 133%;
        display: flex;
        align-items: center;
        color: ${props => props.theme.colors.blue.main};
      }
    }
    h3 {
      font-style: normal;
      font-weight: 400;
      font-size: 16px;
      line-height: 150%;
      margin-top: 12px;

      text-align: center;

      color: ${props => props.theme.colors.gray[4]};
    }
    > div:nth-child(4) {
      margin-top: 12px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 30px;
      > div {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        span {
          font-style: normal;
          font-weight: 400;
          font-size: 14px;
          line-height: 21px;
          color: ${props => props.theme.colors.gray[4]};
        }
      }
      .light {
        > span > img {
          filter: invert(1) !important;
        }
      }
    }
  `,
  ContainerModalImages: styled.div`
    > div:first-child {
      display: grid;
      align-items: center;
      grid-template-columns: 1fr 1fr;
      h2 {
        justify-self: flex-start;
        font-style: normal;
        font-weight: 500;
        font-size: 16px;
        line-height: 100%;
        color: ${props => props.theme.colors.black};
        margin-bottom: 10px;
      }
      > button {
        align-self: center;
        justify-self: flex-end;
        padding: 0;
        cursor: pointer;
        border: none;
        background: transparent;
        outline: none;
        box-shadow: none;
        &[ant-click-animating-without-extra-node='true']::after {
          display: none;
        }
        &:hover,
        &:focus,
        &:active {
          border: none;
          background: transparent;
        }
      }
    }
  `,
  ContainerInfoProfile: styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    > header {
      display: flex;
      align-items: center;
      justify-content: center;
      h2 {
        font-style: normal;
        font-weight: 500;
        font-size: 24px;
        line-height: 36px;
        color: ${props => props.theme.colors.black};
      }
    }
    > form {
      display: flex;
      flex-direction: column;
      gap: 18px;

      div {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 8px;
      }

      div > span {
        color: ${props => props.theme.colors.black};
      }
      div > small {
        color: ${props => props.theme.colors.gray[2]};
      }
      input {
        border-radius: 8px;
        max-width: 624px;
        width: 100%;
        height: 48px;
      }

      .ant-form-item {
        margin-bottom: 0;
      }

      .ant-input-status-error {
        border: 1px solid ${props => props.theme.colors.black};
        color: ${props => props.theme.colors.black};
        background-color: ${props => props.theme.colors.gray[0]} !important;
      }
      .ant-form-item-control-input-content {
        input {
          background-color: ${props => props.theme.colors.gray[0]};
          border: 1px solid ${props => props.theme.colors.gray[1]};
          color: ${props => props.theme.colors.black};
          font-weight: 400;
          font-size: 14px;
          line-height: 21px;
        }
      }
    }
  `,
  TitleModal: styled.span`
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: ${props => props.theme.colors.black};
  `,
  SaveProfile: styled(Button)`
    margin-top: 10px;
    background-color: ${props => props.theme.colors.blue.main};
    border: none;
    border-radius: 8px;
    width: 100%;
    height: 48px;

    &:disabled {
      background: ${props => props.theme.colors.gray[3]};

      &:hover {
        background: ${props => props.theme.colors.gray[3]};
      }
    }

    > span {
      color: ${props => props.theme.colors.constants.white};
      font-weight: 500;
      font-size: 18px;
      line-height: 24px;
    }
  `,
  Copy: styled(CopyToClipboard)`
    cursor: pointer;
    display: flex;
    gap: 10px;
    align-items: center;
    &:hover {
      opacity: 0.8;
    }
  `
}
